import React from "react";
import {
  ResponsiveContainer,
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  ReferenceLine,
  ReferenceArea,
  Tooltip,
} from "recharts";
import { Box, Stack, Typography } from "@mui/material";
import { useTranslation } from "react-i18next";
import theme from "@/theme";
import { convertPxToRem } from "@/utils";
import {
  themeColors,
  backgroundColors,
  CustomDot,
  CustomActiveDot,
  ReferenceRanges,
  CustomTooltipTracker,
  calculateDomain,
  TrendChartSummarySection,
} from "../../../HealthChart/components";
import {
  useChartSelection,
  useTrendChartCompactViewport,
} from "../../../HealthChart/hooks";
import {
  getChartFocusStyles,
  getTrendChartLayoutVitals,
  trendGraphCardHorizontalPaddingSx,
  trendGraphCardPaperSx,
} from "../../../HealthChart/utils";
import { ChartDataPoint } from "../../../HealthChart/types";

// Type alias for breathing rate data points with required breathingRate property
export type BreathingRateDataPoint = ChartDataPoint &
  Required<Pick<ChartDataPoint, "breathingRate">>;

export interface BreathingRateChartProps {
  data: BreathingRateDataPoint[];
}

import { getBreathingRateColor } from "../../../HealthChart/utils/breathingRateChartColors";
export { getBreathingRateColor };

// -------- Main Chart --------
const BreathingRateChart: React.FC<BreathingRateChartProps> = ({ data }) => {
  const { t } = useTranslation();
  const isCompactChart = useTrendChartCompactViewport();
  const { right: chartRightMargin, yAxisWidth } = getTrendChartLayoutVitals(
    isCompactChart,
    22
  );

  // Use shared selection hook
  const {
    setHoveredPoint,
    hasAnimated,
    handlePointClick,
    activePoint,
    activeIndex,
  } = useChartSelection(data);

  if (!activePoint) {
    return null;
  }

  // Calculate dynamic Y-axis domain based on data
  const allBreathingRateValues = data.map((d) => d.breathingRate);
  const yDomain = calculateDomain(allBreathingRateValues, 8, 25, 2);

  // Generate dynamic ticks
  const yTicks = [12, 18, 20];

  const breathingRateChartHeight = 200;
  const breathingRateChartWidth = yAxisWidth;
  const yMargin = { top: 10, bottom: 10 };

  // Reference ranges configuration
  const referenceRanges = [
    {
      label: t("HealthChart.breathingRateChart.zone.high"),
      yValue: 25,
      fontWeight: 700,
      color: themeColors.error,
    },
    {
      label: t("HealthChart.breathingRateChart.zone.elevated"),
      yValue: 23,
      fontWeight: 700,
      color: themeColors.warning,
    },
    {
      label: t("HealthChart.breathingRateChart.zone.normal"),
      yValue: 19.5,
      fontWeight: 700,
      color: themeColors.success,
    },
    {
      label: t("HealthChart.breathingRateChart.zone.low"),
      yValue: 15.5,
      fontWeight: 700,
      color: themeColors.warning,
    },
  ];

  return (
    <>
      <style>{getChartFocusStyles("breathing-rate-chart")}</style>
      <Box className="breathing-rate-chart" sx={trendGraphCardPaperSx}>
        <Stack spacing={2} sx={trendGraphCardHorizontalPaddingSx}>
          {/* Title */}
          <Typography
            variant="h4"
            sx={{
              fontSize: 16,
            }}
          >
            {t("HealthChart.breathingRateChart.title")}
          </Typography>

          {/* Chart Container */}
          <Box
            sx={{
              position: "relative",
              width: "100%",
              height: 200,
              outline: "none",
            }}
          >
            <ResponsiveContainer>
              <LineChart
                data={data}
                margin={{
                  top: 10,
                  right: chartRightMargin,
                  left: 0,
                  bottom: 10,
                }}
                onClick={handlePointClick}
              >
                {/* Grid */}
                <CartesianGrid
                  stroke={theme.palette.grey[300]}
                  strokeDasharray="5 5"
                  vertical={false}
                  horizontal={true}
                />

                {/* Reference Lines and Zones */}
                {/* Background zones */}
                <ReferenceArea
                  y1={20}
                  y2={yDomain[1]}
                  fill={backgroundColors.errorLight}
                  fillOpacity={0.5}
                />
                <ReferenceArea
                  y1={18}
                  y2={20}
                  fill={backgroundColors.warningLight}
                  fillOpacity={0.5}
                />
                <ReferenceArea
                  y1={12}
                  y2={18}
                  fill={backgroundColors.successLight}
                  fillOpacity={0.5}
                />
                <ReferenceArea
                  y1={yDomain[0]}
                  y2={12}
                  fill={backgroundColors.warningLight}
                  fillOpacity={0.5}
                />

                {/* Dashed lines */}
                <ReferenceLine
                  y={20}
                  stroke={themeColors.error}
                  strokeDasharray="3 3"
                  strokeWidth={1}
                />
                <ReferenceLine
                  y={18}
                  stroke={themeColors.warning}
                  strokeDasharray="3 3"
                  strokeWidth={1}
                />
                <ReferenceLine
                  y={12}
                  stroke={themeColors.warning}
                  strokeDasharray="3 3"
                  strokeWidth={1}
                />

                {/* X Axis */}
                <XAxis
                  dataKey="date"
                  tick={{
                    fontSize: 10,
                    fontWeight: 700,
                    fill: theme.palette.grey[700],
                  }}
                  axisLine={{ stroke: theme.palette.grey[500] }}
                  tickLine={false}
                  dy={5}
                />

                <YAxis
                  domain={yDomain}
                  ticks={yTicks}
                  interval={0}
                  tick={{
                    fontSize: 11,
                    fontWeight: 700,
                    fill: theme.palette.grey[700],
                  }}
                  axisLine={{ stroke: theme.palette.grey[500] }}
                  tickLine={false}
                  width={breathingRateChartWidth}
                />

                {/* Tooltip with custom cursor for vertical dashed line - placed before Line so it renders behind dots */}
                <Tooltip
                  content={(props) => (
                    <CustomTooltipTracker
                      {...props}
                      setHoveredPoint={setHoveredPoint}
                    />
                  )}
                  cursor={{
                    stroke: theme.palette.grey[900],
                    strokeWidth: 1,
                    strokeDasharray: "4 4",
                  }}
                />

                {/* Breathing Rate Line */}
                <Line
                  type="linear"
                  dataKey="breathingRate"
                  stroke={theme.palette.grey[300]}
                  strokeWidth={2}
                  dot={(props: any) => (
                    <CustomDot {...props} getColorFn={getBreathingRateColor} />
                  )}
                  activeDot={(props: any) => (
                    <CustomActiveDot
                      {...props}
                      getColorFn={getBreathingRateColor}
                    />
                  )}
                  isAnimationActive={hasAnimated.current}
                  animationDuration={800}
                  animationEasing="ease-in-out"
                />
              </LineChart>
            </ResponsiveContainer>

            {/* Reference Range Labels */}
            <ReferenceRanges
              yDomain={yDomain}
              chartHeight={breathingRateChartHeight}
              yMargin={yMargin}
              ranges={referenceRanges}
            />
          </Box>

          <TrendChartSummarySection
            activeIndex={activeIndex}
            dataLength={data.length}
            chevronLeftMargin={breathingRateChartWidth}
            chevronRightMargin={chartRightMargin}
            chevronOnlyWithMultipleDataPoints={false}
            datePrefix={t("HealthChart.breathingRateChart.datePrefix")}
            fullDate={activePoint.fullDate}
          >
            <div style={{ display: "flex", alignItems: "baseline", gap: 8 }}>
              <span
                style={{
                  fontSize: convertPxToRem(24),
                  fontWeight: 800,
                  color: theme.palette.grey[700],
                }}
              >
                {activePoint.breathingRate}
              </span>
              <span
                style={{
                  fontSize: convertPxToRem(14),
                  color: theme.palette.grey[900],
                  marginLeft: convertPxToRem(4),
                }}
              >
                {t("HealthChart.breathingRateChart.unit")}
              </span>
            </div>
          </TrendChartSummarySection>
        </Stack>
      </Box>
    </>
  );
};

export default BreathingRateChart;
